import type { City, Cell } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import { compressionCapacity } from '../destruction/structure';

interface CapacityCell {
  id: number;
  capacity: number;
}
/** The activation baseline contains only existing, anchored, above-foundation
 * structural cells of civilian buildings. Actual compression capacity weights
 * those IDs; terrain, props, military buildings and later wrecks never enter it.
 * A detached wall/floor no longer supplies usable city capacity, even while its
 * fully damageable rubble persists. Foundations alone do not prevent an end. */
export class CityCapacity {
  baseline = 0;
  usable = 0;
  completedScans = 0;
  capturedMs = 0;
  capturing = false;
  private builder?: Generator<void>;
  private sources = new Map<number, Map<number, Cell>[]>();
  private recorded = new Set<number>();
  private frozen = new Map<number, number>();
  private beforeChange = (cell: Cell): void => {
    if (!this.capturing || this.recorded.has(cell.id) || this.frozen.has(cell.id)) return;
    const sources = this.sources.get(Math.floor(cell.id / 1e6));
    const original = sources?.map((cells) => cells.get(cell.id)).find(Boolean);
    if (original) this.frozen.set(cell.id, this.weight(original));
  };
  private cells: CapacityCell[] = [];
  private cursor = 0;
  private tally = 0;
  private epoch = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
  ) {}
  clear(): void {
    this.physics.cellObservers.delete(this.beforeChange);
    this.sources.clear();
    this.recorded.clear();
    this.frozen.clear();
    this.builder = undefined;
    this.capturing = false;
    this.cells = [];
    this.baseline = this.usable = this.cursor = this.tally = this.completedScans = this.capturedMs = 0;
  }
  capture(): void {
    this.beginCapture();
    while (this.builder)
      if (this.builder.next().done) {
        this.builder = undefined;
        this.capturing = false;
      }
  }
  beginCapture(): void {
    this.clear();
    this.capturing = true;
    this.epoch = this.physics.life.epoch;
    const start = performance.now(),
      military = new Set(this.city.strategic?.parcels.find((p) => p.id === 'military')?.buildings ?? []),
      civilian = new Set(
        this.city.buildings
          .filter((b) => !military.has(b.id) && b.category !== 'prop' && b.category !== 'bridge')
          .map((b) => b.id),
      );
    for (const c of [...this.physics.clusters.values(), ...this.physics.dormant.values()]) {
      if (!c.anchored || !civilian.has(c.building.id)) continue;
      const sources = this.sources.get(c.building.id) ?? [];
      sources.push(c.cells);
      this.sources.set(c.building.id, sources);
    }
    this.physics.cellObservers.add(this.beforeChange);
    this.capturedMs = performance.now() - start;
    this.builder = this.captureCells();
  }
  private weight(cell: Cell): number {
    return !cell.root &&
      cell.y > 0 &&
      ['column', 'beam', 'slab', 'roof', 'wall'].includes(cell.role ?? 'wall') &&
      cell.hp > 0 &&
      (cell.fracture ?? 0) === 0
      ? compressionCapacity(cell)
      : 0;
  }
  private remember(id: number, capacity: number): void {
    if (this.recorded.has(id)) return;
    this.recorded.add(id);
    if (capacity <= 0) return;
    this.cells.push({ id, capacity });
    this.baseline += capacity;
  }
  private *captureCells(): Generator<void> {
    let start = performance.now(),
      scanned = 0;
    // Maps are retained at activation. A mutation observer freezes only changed
    // entries before damage or detachment can alter them; unrelated maps are
    // read in bounded slices. Thus C4 during preparation cannot shift the
    // activation baseline to a later, less damaged or more damaged world.
    for (const sources of this.sources.values())
      for (const cells of sources) {
        for (const original of cells.values()) {
          if (++scanned % 1024 === 0) {
            this.capturedMs += performance.now() - start;
            yield;
            start = performance.now();
          }
          this.remember(original.id, this.frozen.get(original.id) ?? this.weight(original));
        }
      }
    for (const [id, capacity] of this.frozen) {
      this.remember(id, capacity);
      if (++scanned % 1024 === 0) {
        this.capturedMs += performance.now() - start;
        yield;
        start = performance.now();
      }
    }
    this.physics.cellObservers.delete(this.beforeChange);
    this.sources.clear();
    this.recorded.clear();
    this.frozen.clear();
    this.usable = this.baseline;
    this.capturedMs += performance.now() - start;
  }
  update(budget = 4096): void {
    if (this.epoch !== this.physics.life.epoch) {
      this.physics.cellObservers.delete(this.beforeChange);
      this.builder = undefined;
      this.capturing = false;
      this.sources.clear();
      this.recorded.clear();
      this.frozen.clear();
      return;
    }
    if (this.builder) {
      const until = performance.now() + 1;
      for (let i = 0; i < Math.max(1, Math.ceil(budget / 1024)); i++) {
        if (this.builder.next().done) {
          this.builder = undefined;
          this.capturing = false;
          break;
        }
        if (performance.now() >= until) break;
      }
      return;
    }
    if (this.epoch !== this.physics.life.epoch || !this.cells.length) return;
    for (let i = 0; i < Math.min(budget, this.cells.length); i++) {
      const entry = this.cells[this.cursor++],
        owner = this.physics.cellOwner.get(entry.id);
      const cluster =
        owner === undefined
          ? undefined
          : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
      const cell = cluster?.cells.get(entry.id);
      if (cluster?.anchored && cell && cell.hp > 0 && (cell.fracture ?? 0) === 0)
        this.tally += entry.capacity;
      if (this.cursor === this.cells.length) {
        this.usable = this.tally;
        this.tally = 0;
        this.cursor = 0;
        this.completedScans++;
        break;
      }
    }
  }
  get destroyedFraction(): number {
    return this.baseline > 0 ? Math.max(0, Math.min(1, 1 - this.usable / this.baseline)) : 0;
  }
  get lost(): boolean {
    return this.completedScans > 0 && this.destroyedFraction >= 0.99;
  }
  snapshot(): Record<string, number> {
    return {
      cityActivationCapacity: this.baseline,
      cityUsableCapacity: this.usable,
      cityDestroyedPercent: 100 * this.destroyedFraction,
      cityCapacityCells: this.cells.length,
      cityCapacityScans: this.completedScans,
      cityCapacityCaptureMs: this.capturedMs,
      cityCapacityCapturing: this.capturing ? 1 : 0,
    };
  }
}
